// Modern Stytch B2B authentication hook
// Replaces custom AuthContext with native Stytch session management

import { useCallback, useRef, useEffect } from 'react';
import { useStytchMemberSession, useStytchB2BClient, useStytchMember } from '@stytch/react/b2b';

export const useStytchAuth = () => {
  // Use native Stytch hooks - session for auth state, member for user details
  const { session, fromCache } = useStytchMemberSession();
  const { member } = useStytchMember();
  const stytch = useStytchB2BClient();

  // Store latest values in refs to avoid recreating callbacks
  const sessionRef = useRef(session);
  const memberRef = useRef(member);
  const stytchRef = useRef(stytch);

  useEffect(() => {
    sessionRef.current = session;
    memberRef.current = member;
    stytchRef.current = stytch;
  }, [session, member, stytch]);

  // Check if user is authenticated (replaces old isAuthenticated function)
  const isAuthenticated = useCallback(() => {
    return !!sessionRef.current;
  }, []);

  // Get user display name (uses full member object from useStytchMember)
  const getUserDisplayName = useCallback(() => {
    if (!memberRef.current) {
      return 'Unknown User';
    }

    // Use member object from useStytchMember hook - has full member details
    return memberRef.current.name || memberRef.current.email_address || 'Member';
  }, []);

  // Get organization display name
  const getOrganizationDisplayName = useCallback(() => {
    if (!sessionRef.current?.organization_slug && !sessionRef.current?.organization_id) {
      return 'Unknown Org';
    }

    // Use organization_slug if available, otherwise truncated organization_id
    return sessionRef.current.organization_slug || (sessionRef.current.organization_id?.split('-')[0] + '...') || 'Organization';
  }, []);

  // Logout using Stytch client (replaces custom logout)
  const logout = useCallback(async () => {
    if (!stytchRef.current) {
      console.error('Stytch client not available for logout');
      return;
    }

    try {
      await stytchRef.current.session.revoke();
    } catch (error) {
      console.error('Logout error:', error);
      // Don't throw the raw error object - React can't render it
      throw new Error(error?.error_message || error?.message || 'Logout failed');
    }
  }, []);

  return {
    // Session data
    session,
    member, // Full member object from useStytchMember
    memberId: session?.member_id || null,
    organizationId: session?.organization_id || null,
    organizationSlug: session?.organization_slug || null,

    // Loading states
    isLoading: fromCache, // fromCache indicates we're still loading fresh data

    // Functions
    isAuthenticated,
    getUserDisplayName,
    getOrganizationDisplayName,
    logout,

    // Raw Stytch client for advanced usage
    stytch
  };
};